import React, { useState, useRef } from "react";
import Sidebar from "../Common/Sidebar";
import Header from "../Common/Header";
import { useNavigate } from "react-router-dom";
import { Toast } from "primereact/toast";
import axios from "axios";
import { Loader } from "lucide-react";

export default function Register() {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const toast: any = useRef("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [workEmail, setWorkEmail] = useState("");
  const [agencyName, setAgencyName] = useState("");
  const [errorMessageFirstName, setErrorMessageFirstName] = useState("");
  const [errorMessageLastName, setErrorMessageLastName] = useState("");
  const [errorMessageWorkEmail, setErrorMessageWorkEmail] = useState("");
  const [errorMessageAgencyName, setErrorMessageAgencyName] = useState("");
  const [loading, setLoading] = useState(false);
  // How did you hear about the assessment
  const [heardAbout, setHeardAbout] = useState("");
  const [errorMessageHeardAbout, setErrorMessageHeardAbout] = useState("");

  const checkValidation = () => {
    let formIsValid = true;

    setErrorMessageFirstName("");
    setErrorMessageLastName("");
    setErrorMessageWorkEmail("");
    setErrorMessageAgencyName("");
    setErrorMessageHeardAbout("");

    if (!firstName.trim()) {
      setErrorMessageFirstName("Please enter first name");
      formIsValid = false;
    }
    if (!lastName.trim()) {
      setErrorMessageLastName("Please enter last name");
      formIsValid = false;
    }
    if (!workEmail.trim()) {
      setErrorMessageWorkEmail("Please enter work email");
      formIsValid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(workEmail)) {
      setErrorMessageWorkEmail("Please enter a valid email address");
      formIsValid = false;
    }

    // --- Agency Info Validation ---
    if (!agencyName.trim()) {
      setErrorMessageAgencyName("Please enter agency name");
      formIsValid = false;
    }

    // Heard About validation (optional unless Other chosen)
    if (!heardAbout.trim()) {
      setErrorMessageHeardAbout("Please select an option");
      formIsValid = false;
    }

    return formIsValid;
  };

  const onRegisterClick = () => {
    if (checkValidation()) {
      setLoading(true);
      var data = {
        userName: workEmail,
        workEmail: workEmail,
        agencyName: agencyName,
        firstName: firstName,
        lastName: lastName,
        heardFrom: heardAbout,
      };
      var config = {
        method: "post",
        url: `${process.env.REACT_APP_MAPSAPI}/api/account/register`,
        headers: {
          "Content-Type": "Application/json",
        },
        data: data,
      };

      axios(config)
        .then((response: any) => {
          if (response.status === 200) {
            setLoading(false);
            toast.current.show({
              severity: "success",
              summary: "Success",
              detail: "User registered successfully.",
              life: 3000,
            });
            localStorage.setItem("igtoken", response.data.token);
            localStorage.setItem("userName", response.data.userName);
            localStorage.setItem("AvatarName", response.data.avatarName);

            navigate("/scale");
          }
        })
        .catch((error: any) => {
          console.log(error);

          setLoading(false);
          if (error.status == 409) {
            toast.current.show({ severity: "error", summary: "Error", detail: "User already registered with this email.", life: 3000 });
          } else {
            toast.current.show({ severity: "error", summary: "Error", detail: "Unknown error while registering the new user.", life: 3000 });
          }
        });
    }
  };

  const onSidebarClick = (val: any) => {
    setSidebarOpen(val);
  };

  return (
    <div className="flex min-h-screen bg-gray-100">
      <Toast ref={toast} />
      <Sidebar pagename="agency-information" onSidebarClick={() => onSidebarClick(false)} sidebarOpen={sidebarOpen} />

      <main className="flex-1 p-4 md:p-8 flex flex-col">
        <Header onSidebarClick={() => onSidebarClick(true)} />

        <div className="flex flex-1 items-center justify-center">
          <div className="w-full bg-white rounded-xl shadow-md p-8 max-w-4xl">
            <h2 className="text-2xl font-bold mb-6 text-center">Registration</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* First Name */}
              <div>
                <label className="block font-medium mb-1">First Name <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="w-full border rounded-lg p-2 focus:ring-2 focus:ring-green-400"
                />
                {errorMessageFirstName && <p className="text-red-500 text-sm mt-1">{errorMessageFirstName}</p>}
              </div>

              {/* Last Name */}
              <div>
                <label className="block font-medium mb-1">Last Name <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="w-full border rounded-lg p-2 focus:ring-2 focus:ring-green-400"
                />
                {errorMessageLastName && <p className="text-red-500 text-sm mt-1">{errorMessageLastName}</p>}
              </div>

              {/* Work Email */}
              <div className="md:col-span-2">
                <label className="block font-medium mb-1">Work Email <span className="text-red-500">*</span></label>
                <input
                  type="email"
                  value={workEmail}
                  onChange={(e) => setWorkEmail(e.target.value)}
                  className="w-full border rounded-lg p-2 focus:ring-2 focus:ring-green-400"
                />
                {errorMessageWorkEmail && <p className="text-red-500 text-sm mt-1">{errorMessageWorkEmail}</p>}
              </div>

              {/* Agency Name */}
              <div className="md:col-span-2">
                <label className="block font-medium mb-1">Agency Name <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={agencyName}
                  onChange={(e) => setAgencyName(e.target.value)}
                  className="w-full border rounded-lg p-2 focus:ring-2 focus:ring-green-400"
                />
                {errorMessageAgencyName && <p className="text-red-500 text-sm mt-1">{errorMessageAgencyName}</p>}
              </div>

              {/* How did you hear about the assessment */}
              <div className="md:col-span-2">
                <label className="block font-medium mb-1">How did you hear about the assessment? <span className="text-red-500">*</span></label>
                <select
                  value={heardAbout}
                  onChange={(e) => {
                    setHeardAbout(e.target.value);
                  }}
                  className="w-full border rounded-lg p-2 bg-white focus:ring-2 focus:ring-green-400"
                >
                  <option value="">Select an option</option>
                  <option value="Social Media">1. Social Media</option>
                  <option value="Referral">2. Referral</option>
                  <option value="Association Member">3. Association Member</option>
                  <option value="Other">4. Other</option>
                </select>
                {errorMessageHeardAbout && <p className="text-red-500 text-sm mt-1">{errorMessageHeardAbout}</p>}
              </div>
            </div>

            {/* Register Button */}
            {loading ? (
              <div className="flex justify-center mt-4">
                <Loader className="h-8 w-8 text-green-500 animate-spin" />
              </div>
            ) : (
              <div className="flex justify-center mt-8">
                <button
                  type="button"
                  onClick={onRegisterClick}
                  className="px-8 bg-green-500 hover:bg-green-600 text-white font-semibold py-3 rounded-lg"
                >
                  Register
                </button>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
